import React, { Component } from 'react';
import getApiBaseUrl from '../utils/apiConfig';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import AsyncStorage from '@react-native-async-storage/async-storage';
import '../App.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || getApiBaseUrl();

export class Activate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      validating: true,
      tokenValid: false,

      // Store info from token
      storeId: '',
      storeName: '',
      email: '',

      // Password creation
      password: '',
      passwordConfirm: '',
      activating: false,

      // Error handling
      error: null,

      // Password validation indicators
      hasMinLength: false,
      hasNumber: false,
      hasLetter: false,
      passwordsMatch: false,

      // Password visibility toggles
      showPassword: false,
      showPasswordConfirm: false,

      // Token entry (for /activate without token)
      tokenInput: ''
    };

    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.handlePasswordConfirmChange = this.handlePasswordConfirmChange.bind(this);
    this.handleActivate = this.handleActivate.bind(this);
    this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
    this.togglePasswordConfirmVisibility = this.togglePasswordConfirmVisibility.bind(this);
    this.handleTokenSubmit = this.handleTokenSubmit.bind(this);
  }

  async componentDidMount() {
    await this.validateToken();
  }

  /**
   * Extract token from URL
   */
  getTokenFromUrl() {
    // For web
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const match = path.match(/\/activate\/(.+)/);
      return match ? match[1] : null;
    }
    return null;
  }

  /**
   * Handle token/link input submission
   */
  handleTokenSubmit() {
    try {
      const input = this.state.tokenInput.trim();
      console.log('🔗 handleTokenSubmit called, input:', input);

      if (!input) {
        window.alert('Please enter your activation link or token');
        return;
      }

      // Check if it's a full URL or just a token
      let token;
      if (input.includes('/activate/')) {
        // Extract token from full URL
        console.log('   Extracting token from full URL...');
        const match = input.match(/\/activate\/(.+?)(?:[?#]|$)/);
        token = match ? match[1] : null;
        console.log('   Match result:', match);
        console.log('   Extracted token:', token);
      } else {
        // Assume it's just the token
        console.log('   Using input as token directly');
        token = input;
      }

      if (!token) {
        console.error('   ❌ No token extracted');
        window.alert('Invalid activation link or token');
        return;
      }

      console.log('   ✅ Navigating to /activate/' + token.substring(0, 20) + '...');

      // Navigate to activation page with token
      window.location.href = `/activate/${token}`;
    } catch (error) {
      console.error('❌ Error in handleTokenSubmit:', error);
      window.alert('Error processing activation link: ' + error.message);
    }
  }

  /**
   * Validate activation token with backend
   */
  async validateToken() {
    try {
      const token = this.getTokenFromUrl();

      if (!token) {
        // No token in URL - show token entry form instead of error
        this.setState({
          validating: false,
          tokenValid: false,
          loading: false
        });
        return;
      }

      // Validate with backend
      const response = await fetch(`${API_BASE}/activation/validate/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.valid) {
        this.setState({
          validating: false,
          tokenValid: true,
          storeId: data.storeId,
          storeName: data.storeName,
          email: data.email || ''
        });
      } else {
        this.setState({
          validating: false,
          tokenValid: false,
          error: data.error || 'Invalid activation token'
        });
      }

    } catch (error) {
      console.error('Token validation error:', error);
      this.setState({
        validating: false,
        tokenValid: false,
        error: 'Failed to validate token. Please check your connection.'
      });
    }
  }

  /**
   * Handle password input change
   */
  handlePasswordChange(e) {
    const password = e.target.value;
    const { passwordConfirm } = this.state;

    this.setState({
      password,
      hasMinLength: password.length >= 8,
      hasNumber: /\d/.test(password),
      hasLetter: /[a-zA-Z]/.test(password),
      passwordsMatch: password === passwordConfirm && password.length > 0
    });
  }

  /**
   * Handle password confirmation change
   */
  handlePasswordConfirmChange(e) {
    const passwordConfirm = e.target.value;
    const { password } = this.state;

    this.setState({
      passwordConfirm,
      passwordsMatch: password === passwordConfirm && password.length > 0
    });
  }

  /**
   * Toggle password visibility
   */
  togglePasswordVisibility() {
    this.setState({ showPassword: !this.state.showPassword });
  }

  /**
   * Toggle password confirm visibility
   */
  togglePasswordConfirmVisibility() {
    this.setState({ showPasswordConfirm: !this.state.showPasswordConfirm });
  }

  /**
   * Validate password meets requirements
   */
  validatePassword() {
    const { password, passwordConfirm, hasMinLength, hasNumber, hasLetter } = this.state;

    if (!hasMinLength) {
      window.alert('Password must be at least 8 characters long');
      return false;
    }

    if (password !== passwordConfirm) {
      window.alert('Passwords do not match');
      return false;
    }

    if (!hasNumber || !hasLetter) {
      window.alert('Password should contain both letters and numbers for better security');
      return false;
    }

    return true;
  }

  /**
   * Clear all previous store data from AsyncStorage
   * CRITICAL: Prevents wallet/data mixing between different stores
   */
  async clearPreviousStoreData() {
    const keysToRemove = [
      // Wallet private keys (encrypted)
      'secure_api-key-btc',
      'secure_api-key-ltc',
      'secure_api-key-doge',

      // Legacy unencrypted keys (if they exist)
      'api-key-btc',
      'api-key-ltc',
      'api-key-doge',
      'api-key',

      // Public keys (xpubs) - MUST match actual key names used by app
      'api-xpub-btc',
      'api-xpub-ltc',
      'api-xpub-doge',

      // Payout addresses
      'retailerPayoutAddress',

      // Store settings
      'store-name',
      'posMode',
      'merchantId',
      'tipsMode',
      'discount',
      'splash',
      'fiatCurrency',
      'fiatSymbol',
      'topText',
      'middleText',
      'bottomText'
    ];

    // Remove all old store data
    for (const key of keysToRemove) {
      try {
        await AsyncStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key}:`, error);
      }
    }

    console.log('✅ Cleared previous store data from AsyncStorage');
  }

  /**
   * Complete activation - send password to backend
   */
  async handleActivate() {
    // Validate password
    if (!this.validatePassword()) {
      return;
    }

    this.setState({ activating: true });

    try {
      const token = this.getTokenFromUrl();
      const { password } = this.state;

      const response = await fetch(`${API_BASE}/activation/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (data.success) {
        // CRITICAL: Clear all previous store data BEFORE saving new store
        // This prevents wallet mixing between different stores
        await this.clearPreviousStoreData();

        // Save credentials to AsyncStorage
        await AsyncStorage.setItem('store-id', data.storeId);
        await AsyncStorage.setItem('storePassword', password);

        // Show success message
        window.alert(
          'Activation Complete! 🎉\n\n' +
          'Welcome to Dijitto Express!\n\n' +
          'Your store is now activated and ready to accept crypto payments.'
        );

        // Navigate to Admin
        window.location.href = '/admin';
      } else {
        window.alert('Activation Failed\n\n' + (data.error || 'Failed to activate store'));
        this.setState({ activating: false });
      }

    } catch (error) {
      console.error('Activation error:', error);
      window.alert('Activation Failed\n\nNetwork error. Please try again.');
      this.setState({ activating: false });
    }
  }

  /**
   * Render loading state
   */
  renderLoading() {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Validating activation link...</p>
        </div>
      </Container>
    );
  }

  /**
   * Render token entry form (when no token in URL)
   * Matches the exact design from StoreRequest.js success screen
   */
  renderTokenEntry() {
    const { tokenInput } = this.state;
    const activating = false; // Not used in this simplified version
    const activationError = ''; // We'll navigate instead of showing inline errors

    return (
      <Container fluid className="App">
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={6}>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              {/* Blue Box - Exact match to StoreRequest.js */}
              <div style={{
                border: '2px solid #007bff',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                backgroundColor: '#f0f8ff',
                textAlign: 'left'
              }}>
                <h4 style={{ color: '#007bff', marginBottom: '15px', textAlign: 'center' }}>
                  🎉 Already Approved?
                </h4>
                <p style={{ fontSize: '14px', marginBottom: '15px', textAlign: 'center' }}>
                  If your request was approved, enter your activation code below to get started immediately:
                </p>

                <input
                  type="text"
                  value={tokenInput}
                  onChange={(e) => this.setState({ tokenInput: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && tokenInput.trim()) {
                      this.handleTokenSubmit();
                    }
                  }}
                  placeholder="Enter activation code or paste full URL"
                  disabled={activating}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: activationError ? '2px solid #dc3545' : '1px solid #ced4da',
                    borderRadius: '4px',
                    marginBottom: '10px'
                  }}
                />

                {activationError && (
                  <div style={{
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    padding: '10px',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    fontSize: '14px'
                  }}>
                    {activationError}
                  </div>
                )}

                <button
                  onClick={this.handleTokenSubmit}
                  disabled={activating || !tokenInput.trim()}
                  style={{
                    width: '100%',
                    backgroundColor: activating || !tokenInput.trim() ? '#6c757d' : '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: activating || !tokenInput.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {activating ? 'Activating...' : 'Activate Now'}
                </button>

                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px', textAlign: 'center' }}>
                  💡 Tip: You can paste the full activation URL or just the code
                </p>
              </div>

              {/* Return to Home Section - Exact match to StoreRequest.js */}
              <div style={{
                borderTop: '1px solid #ddd',
                paddingTop: '20px'
              }}>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                  ⏱️ Still waiting for approval?
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '12px 30px',
                    borderRadius: '6px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Return to Home
                </button>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  /**
   * Render error state
   */
  renderError() {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <div className="text-center">
              <div style={{ fontSize: '64px' }}>⚠️</div>
              <h2 className="mt-3">Activation Link Invalid</h2>
              <Alert variant="danger" className="mt-4 text-start">
                <Alert.Heading>Error</Alert.Heading>
                <p>{this.state.error}</p>
              </Alert>

              <div className="text-start mt-4">
                <h5>Possible reasons:</h5>
                <ul>
                  <li>Link has expired (valid for 7 days)</li>
                  <li>Link has already been used</li>
                  <li>Link is malformed or incorrect</li>
                </ul>
              </div>

              <Button
                variant="primary"
                className="mt-4"
                onClick={() => window.alert('Please contact support@dijitto.com')}
              >
                Contact Support
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  /**
   * Render activation form
   */
  renderActivationForm() {
    const {
      storeName,
      storeId,
      password,
      passwordConfirm,
      activating,
      hasMinLength,
      hasNumber,
      hasLetter,
      passwordsMatch,
      showPassword,
      showPasswordConfirm
    } = this.state;

    return (
      <Container className="mt-5 mb-5">
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            {/* Welcome Header */}
            <div className="text-center mb-5">
              <div style={{ fontSize: '64px' }}>🎉</div>
              <h1 className="mt-3">Welcome to Dijitto Express!</h1>
              <h3 className="text-primary">{storeName}</h3>
            </div>

            {/* Store Info Card */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <h6 className="text-muted">Your Store ID</h6>
                <h3 className="text-primary mb-2">{storeId}</h3>
                <p className="text-muted small mb-0">
                  <i>Keep this ID safe - you'll need it if you ever reinstall the app</i>
                </p>
              </div>
            </div>

            {/* Password Creation Section */}
            <div className="card shadow-sm">
              <div className="card-body">
                <h4 className="mb-2">Create Your Secure Password</h4>
                <p className="text-muted mb-4">
                  This password will encrypt your crypto wallet keys
                </p>

                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter password (min 8 characters)"
                        value={password}
                        onChange={this.handlePasswordChange}
                        disabled={activating}
                        style={{ paddingRight: '45px' }}
                      />
                      <button
                        type="button"
                        onClick={this.togglePasswordVisibility}
                        disabled={activating}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: activating ? 'default' : 'pointer',
                          fontSize: '20px',
                          padding: '5px',
                          opacity: activating ? 0.5 : 1
                        }}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <div style={{ position: 'relative' }}>
                      <Form.Control
                        type={showPasswordConfirm ? "text" : "password"}
                        placeholder="Confirm password"
                        value={passwordConfirm}
                        onChange={this.handlePasswordConfirmChange}
                        disabled={activating}
                        style={{ paddingRight: '45px' }}
                      />
                      <button
                        type="button"
                        onClick={this.togglePasswordConfirmVisibility}
                        disabled={activating}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: activating ? 'default' : 'pointer',
                          fontSize: '20px',
                          padding: '5px',
                          opacity: activating ? 0.5 : 1
                        }}
                        aria-label={showPasswordConfirm ? "Hide password" : "Show password"}
                      >
                        {showPasswordConfirm ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </Form.Group>

                  {/* Password Requirements */}
                  <Alert variant="light" className="mb-4">
                    <strong>Password Requirements:</strong>
                    <ul className="mb-0 mt-2" style={{ listStyle: 'none', paddingLeft: 0 }}>
                      <li>{hasMinLength ? '✅' : '⭕'} At least 8 characters</li>
                      <li>{hasNumber ? '✅' : '⭕'} Contains a number</li>
                      <li>{hasLetter ? '✅' : '⭕'} Contains a letter</li>
                      <li>{passwordsMatch ? '✅' : '⭕'} Passwords match</li>
                    </ul>
                  </Alert>

                  <Button
                    variant="primary"
                    size="lg"
                    className="w-100"
                    onClick={this.handleActivate}
                    disabled={activating || !hasMinLength || !hasNumber || !hasLetter || !passwordsMatch}
                  >
                    {activating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Activating...
                      </>
                    ) : (
                      'Activate My Store'
                    )}
                  </Button>
                </Form>
              </div>
            </div>

            {/* Security Note */}
            <Alert variant="warning" className="mt-4">
              <div className="d-flex align-items-start">
                <span style={{ fontSize: '24px', marginRight: '10px' }}>🔐</span>
                <div>
                  <strong>Security Note:</strong> Your password encrypts your wallet keys and never leaves your device.
                  We cannot recover it if you forget it, so please keep it safe!
                </div>
              </div>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  render() {
    const { validating, tokenValid, loading } = this.state;

    if (validating || loading) {
      return this.renderLoading();
    }

    // If no token in URL, show token entry form
    if (!this.getTokenFromUrl()) {
      return this.renderTokenEntry();
    }

    if (!tokenValid) {
      return this.renderError();
    }

    return this.renderActivationForm();
  }
}

export default Activate;
